import { apiJson } from '../api';
import type { AdminStory, AdminStoryDetail, ArticleChange, ArticleChangeDetail, ArticleJudgments, ArticleScoreDerivation, StoryFilterQuery, TopicDetail } from '../types';

export async function filterStories(query: StoryFilterQuery): Promise<AdminStory[]> {
  const data = await apiJson<AdminStory[]>('/api/admin/stories/filter', {
    method: 'POST',
    json: query,
  });
  return Array.isArray(data) ? data : [];
}

export async function getStory(storyId: string): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}`);
}

// With a url this is a complete server-side ingest: the backend fetches
// content and queues enrichment itself, so no follow-up fetch-content/enrich
// calls are needed. Omit title to let the fetched page title win.
export async function createStory(data: {
  title?: string;
  url?: string;
  tier?: number;
  note?: string;
}): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>('/api/admin/stories', {
    method: 'POST',
    json: data,
  });
}

export async function updateStory(storyId: string, data: {
  title?: string;
  description?: string;
  url?: string;
  linked_url?: string;
}): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}`, {
    method: 'PATCH',
    json: data,
  });
}

export async function fetchStoryContent(storyId: string): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}/fetch-content`, {
    method: 'POST',
  });
}

export async function enrichStory(storyId: string): Promise<{ message: string }> {
  return apiJson<{ message: string }>(`/api/admin/stories/${encodeURIComponent(storyId)}/enrich`, {
    method: 'POST',
  });
}

export async function getStoryChanges(storyId: string): Promise<ArticleChange[]> {
  return apiJson<ArticleChange[]>(`/api/admin/stories/${encodeURIComponent(storyId)}/changes`);
}

export async function getArticleJudgments(storyId: string): Promise<ArticleJudgments> {
  return apiJson<ArticleJudgments>(`/api/admin/articles/${encodeURIComponent(storyId)}/judgments`);
}

export async function getArticleScoreDerivation(storyId: string): Promise<ArticleScoreDerivation> {
  return apiJson<ArticleScoreDerivation>(`/api/admin/articles/${encodeURIComponent(storyId)}/score-derivation`);
}

export type ArticleMaintenanceAction = 'fetch_content' | 'enrich' | 'create';

/** Report a use of an admin article page Maintenance control. The backend logs
 * it and emits the metric the kingfisher-article-admin-maintenance-used alarm
 * watches; the page awaits it before running the action. */
export async function recordMaintenanceUse(action: ArticleMaintenanceAction, articleId: string | null): Promise<void> {
  await apiJson('/api/admin/article-maintenance-used', {
    method: 'POST',
    json: { action, article_id: articleId },
  });
}

export async function getChange(changeId: string): Promise<ArticleChangeDetail> {
  return apiJson<ArticleChangeDetail>(`/api/admin/changes/${encodeURIComponent(changeId)}`);
}

export async function getTopic(name: string): Promise<TopicDetail> {
  return apiJson<TopicDetail>(`/api/v1/topics/${encodeURIComponent(name)}`);
}
